import { createFileRoute, Link } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  ClipboardList,
  DollarSign,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  Plus,
  Sparkles,
  Star,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { EditFieldsDialog } from "@/components/clients/EditFieldsDialog";
import { ClientActivityTab } from "@/components/clients/ClientActivityTab";
import { PublicBalanceCard } from "@/components/clients/PublicBalanceCard";
import { ClientPicker } from "@/components/clients/ClientPicker";
import {
  SOURCE_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  HAND_OPTIONS,
  STATUS_OPTIONS,
  RATING_VALUES,
  fmtRating,
  sourceLabel,
} from "@/lib/clients";

export const Route = createFileRoute("/app/clients_/$clientId")({
  head: ({ params }) => ({
    meta: buildAppMeta({
      title: "Client details — CoachQuick",
      description: "Client profile with session history, drills, homework, invoices, and AI-drafted recaps.",
      path: `/app/clients/${params.clientId}`,
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/clients/${params.clientId}` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <ClientProfile />
    </RequireAuth>
  ),
});

type SessionRow = {
  id: string;
  session_date: string;
  status: "draft" | "sent";
  notes: string | null;
};

type DrillRow = {
  id: string;
  session_id: string;
  is_homework: boolean;
  skill_level_used: "basic" | "l2" | "l3" | null;
  drills: {
    id: string;
    title: string;
    item_type: "technique" | "drill" | "recommendation" | "resource";
    skill_level: "basic" | "l2" | "l3" | null;
    video_url: string | null;
  } | null;
};

type SummaryRow = {
  session_id: string;
  sent_at: string | null;
  ai_draft: string | null;
  final_text: string | null;
};

type PaymentRow = {
  id: string;
  session_id: string;
  amount_cents: number;
  currency: string;
  method: string | null;
  paid_at: string | null;
  notes: string | null;
};

const METHODS = ["cash", "venmo", "zelle", "check", "card", "other"] as const;

type Tab = "sessions" | "notes" | "skills" | "invoices" | "files" | "activity";

const AVATAR_PALETTE = [
  "bg-[#9fe870] text-[#054d28]",
  "bg-[#7dd3fc] text-[#075985]",
  "bg-[#fcd34d] text-[#78350f]",
  "bg-[#fdba74] text-[#7c2d12]",
  "bg-[#f0abfc] text-[#701a75]",
  "bg-[#a5b4fc] text-[#312e81]",
  "bg-[#fda4af] text-[#881337]",
];

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function paletteFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function fmtMoney(cents: number, currency = "USD") {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
    (cents ?? 0) / 100,
  );
}

function daysAgo(dateStr: string) {
  const d = new Date(dateStr);
  const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "Today";
  if (diff === 1) return "1d";
  if (diff < 30) return `${diff}d`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo`;
  return `${Math.floor(diff / 365)}y`;
}

function parseIsoDate(s: string): { day: number; monthShort: string; full: string } | null {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return {
    day: d,
    monthShort: date.toLocaleString(undefined, { month: "short" }),
    full: date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
  };
}

function ClientProfile() {
  const { clientId } = Route.useParams();
  const { user } = useAuth();
  const [client, setClient] = useState<any>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [drills, setDrills] = useState<DrillRow[]>([]);
  const [summaries, setSummaries] = useState<SummaryRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notesDraft, setNotesDraft] = useState("");
  const [rateDraft, setRateDraft] = useState("");
  const [payDialog, setPayDialog] = useState<{ sessionId: string; existing?: PaymentRow } | null>(
    null,
  );
  const [editCard, setEditCard] = useState<null | "contact" | "rating" | "lifecycle">(null);
  const [relations, setRelations] = useState<
    Array<{
      id: string;
      related_client_id: string;
      relationship_label: string;
      related: { id: string; student_name: string } | null;
    }>
  >([]);
  const [referrer, setReferrer] = useState<{ id: string; student_name: string } | null>(null);
  const [addRelOpen, setAddRelOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("sessions");

  async function load() {
    if (!user) return;
    setLoading(true);
    // The database decides visibility: on Starter, clients outside the 5 most
    // recent come back as { id, locked: true, student_name } and nothing else.
    const { data: cRaw } = await supabase.rpc("get_my_client" as never, {
      _client_id: clientId,
    } as never);
    const c = (cRaw as any) ?? null;
    setClient(c);
    if (c?.locked) {
      setLoading(false);
      return;
    }
    setNotesDraft((c as any)?.notes ?? "");
    setRateDraft(
      (c as any)?.default_rate_cents ? String(((c as any).default_rate_cents / 100).toFixed(2)) : "",
    );

    const refId = (c as any)?.referred_by_client_id as string | null;
    const [{ data: rel }, refData] = await Promise.all([
      supabase
        .from("coach_student_relations" as never)
        .select("id, related_client_id, relationship_label")
        .eq("client_id", clientId),
      refId
        ? supabase.from("coach_students").select("id, student_name").eq("id", refId).maybeSingle()
        : Promise.resolve({ data: null } as any),
    ]);
    const relList = (rel ?? []) as any[];
    if (relList.length > 0) {
      const ids = relList.map((r: any) => r.related_client_id);
      const { data: people } = await supabase
        .from("coach_students")
        .select("id, student_name")
        .in("id", ids);
      const byId = new Map((people ?? []).map((p: any) => [p.id, p]));
      setRelations(
        relList.map((r: any) => ({ ...r, related: byId.get(r.related_client_id) ?? null })),
      );
    } else {
      setRelations([]);
    }
    setReferrer((refData as any)?.data ?? null);

    const { data: s } = await supabase
      .from("sessions")
      .select("id, session_date, status, notes")
      .eq("coach_student_id", clientId)
      .order("session_date", { ascending: false });
    const sessionList = (s ?? []) as SessionRow[];
    setSessions(sessionList);

    const sessionIds = sessionList.map((x) => x.id);
    if (sessionIds.length > 0) {
      const [{ data: sd }, { data: sum }, { data: pay }] = await Promise.all([
        supabase
          .from("session_drills")
          .select(
            "id, session_id, is_homework, skill_level_used, drills(id, title, item_type, skill_level, video_url)",
          )
          .in("session_id", sessionIds),
        supabase
          .from("summaries")
          .select("session_id, sent_at, ai_draft, final_text")
          .in("session_id", sessionIds),
        supabase.from("session_payments" as never).select("*").in("session_id", sessionIds),
      ]);
      setDrills((sd ?? []) as any);
      setSummaries((sum ?? []) as any);
      setPayments((pay ?? []) as any);
    } else {
      setDrills([]);
      setSummaries([]);
      setPayments([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [user, clientId]);

  const stats = useMemo(() => {
    const totalLessons = sessions.length;
    const totalPaidCents = payments
      .filter((p) => p.paid_at)
      .reduce((sum, p) => sum + (p.amount_cents ?? 0), 0);
    const lastSeen = sessions[0]?.session_date ?? null;
    const earliest = sessions[sessions.length - 1]?.session_date ?? null;

    const weekKeys = new Set(
      sessions.map((s) => {
        const d = new Date(s.session_date);
        const onejan = new Date(d.getFullYear(), 0, 1);
        const week = Math.ceil(
          ((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7,
        );
        return `${d.getFullYear()}-${week}`;
      }),
    );
    let streak = 0;
    const now = new Date();
    for (let i = 0; i < 104; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - i * 7);
      const onejan = new Date(d.getFullYear(), 0, 1);
      const week = Math.ceil(
        ((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7,
      );
      if (weekKeys.has(`${d.getFullYear()}-${week}`)) streak++;
      else if (i > 0) break;
    }

    return { totalLessons, totalPaidCents, lastSeen, earliest, streak };
  }, [sessions, payments]);

  const topAreas = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of drills) {
      const t = d.drills?.title;
      if (!t) continue;
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [drills]);

  const coverage = useMemo(() => {
    const types: Record<string, number> = {
      technique: 0,
      drill: 0,
      recommendation: 0,
      resource: 0,
    };
    for (const d of drills) {
      const t = d.drills?.item_type;
      if (t && t in types) types[t]++;
    }
    return types;
  }, [drills]);

  const skillProgression = useMemo(() => {
    const map = new Map<string, { date: string; level: "basic" | "l2" | "l3" | null }[]>();
    const sessDateById = new Map(sessions.map((s) => [s.id, s.session_date]));
    for (const d of drills) {
      if (!d.drills || d.drills.item_type !== "technique") continue;
      const date = sessDateById.get(d.session_id);
      if (!date) continue;
      const arr = map.get(d.drills.title) ?? [];
      arr.push({ date, level: d.skill_level_used ?? d.drills.skill_level });
      map.set(d.drills.title, arr);
    }
    return Array.from(map.entries())
      .filter(([, list]) => list.length >= 2)
      .map(([title, list]) => [title, list.sort((a, b) => a.date.localeCompare(b.date))] as const)
      .slice(0, 6);
  }, [drills, sessions]);

  const recapStatus = useMemo(() => {
    const sumBySession = new Map(summaries.map((s) => [s.session_id, s]));
    let sent = 0,
      drafted = 0,
      none = 0;
    for (const s of sessions) {
      const sum = sumBySession.get(s.id);
      if (sum?.sent_at) sent++;
      else if (sum?.ai_draft || sum?.final_text) drafted++;
      else none++;
    }
    return { sent, drafted, none };
  }, [sessions, summaries]);

  const monthlyRevenue = useMemo(() => {
    const months: { key: string; label: string; cents: number }[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months.push({ key, label: d.toLocaleDateString(undefined, { month: "short" }), cents: 0 });
    }
    for (const p of payments) {
      if (!p.paid_at) continue;
      const d = new Date(p.paid_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const m = months.find((x) => x.key === key);
      if (m) m.cents += p.amount_cents ?? 0;
    }
    const max = Math.max(1, ...months.map((m) => m.cents));
    return { months, max };
  }, [payments]);

  const sessionMeta = useMemo(() => {
    const drillsBySession = new Map<string, DrillRow[]>();
    for (const d of drills) {
      const arr = drillsBySession.get(d.session_id) ?? [];
      arr.push(d);
      drillsBySession.set(d.session_id, arr);
    }
    const paymentBySession = new Map(payments.map((p) => [p.session_id, p]));
    const summaryBySession = new Map(summaries.map((s) => [s.session_id, s]));
    return { drillsBySession, paymentBySession, summaryBySession };
  }, [drills, payments, summaries]);

  const skillsList = useMemo(() => {
    const map = new Map<
      string,
      {
        title: string;
        type: string;
        count: number;
        lastDate: string;
        level: string | null;
        videoUrl: string | null;
      }
    >();
    const sessDateById = new Map(sessions.map((s) => [s.id, s.session_date]));
    for (const d of drills) {
      if (!d.drills) continue;
      const key = d.drills.id;
      const date = sessDateById.get(d.session_id) ?? "";
      const existing = map.get(key);
      if (existing) {
        existing.count++;
        if (date > existing.lastDate) {
          existing.lastDate = date;
          existing.level = d.skill_level_used ?? d.drills.skill_level;
        }
      } else {
        map.set(key, {
          title: d.drills.title,
          type: d.drills.item_type,
          count: 1,
          lastDate: date,
          level: d.skill_level_used ?? d.drills.skill_level,
          videoUrl: d.drills.video_url,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [drills, sessions]);

  async function saveNotes() {
    const { error } = await supabase
      .from("coach_students")
      .update({ notes: notesDraft } as never)
      .eq("id", clientId);
    if (error) toast.error(error.message);
    else toast.success("Notes saved");
  }

  async function saveRate() {
    const cents = rateDraft.trim() === "" ? null : Math.round(parseFloat(rateDraft) * 100);
    if (rateDraft.trim() !== "" && (Number.isNaN(cents!) || cents! < 0)) {
      toast.error("Enter a valid amount");
      return;
    }
    const { error } = await supabase
      .from("coach_students")
      .update({ default_rate_cents: cents } as never)
      .eq("id", clientId);
    if (error) toast.error(error.message);
    else {
      toast.success("Default rate saved");
      load();
    }
  }

  if (loading || !client) {
    return (
      <AppShell>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </AppShell>
    );
  }

  if ((client as any).locked) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md rounded-[16px] border bg-card p-8 text-center">
          <h1 className="text-lg font-semibold">{client.student_name}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Unlock your full client list — upgrade to Pro to see notes, history, and contact info
            for all of your clients.
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/app/clients">Back to clients</Link>
            </Button>
            <Button asChild>
              <Link to="/app/settings/billing" search={{}}>
                See plans
              </Link>
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  const totalOutstandingCents = sessions
    .filter((s) => {
      const p = sessionMeta.paymentBySession.get(s.id);
      return p && !p.paid_at;
    })
    .reduce((sum, s) => sum + (sessionMeta.paymentBySession.get(s.id)?.amount_cents ?? 0), 0);

  const paidCount = payments.filter((p) => p.paid_at).length;
  const unpaidCount = payments.filter((p) => !p.paid_at).length;
  const avgPerSession = paidCount ? stats.totalPaidCents / paidCount : 0;

  const lastSeenParsed = stats.lastSeen ? parseIsoDate(stats.lastSeen) : null;
  const earliestParsed = stats.earliest ? parseIsoDate(stats.earliest) : null;
  const earliestLabel = earliestParsed
    ? new Date(stats.earliest!).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : null;

  const statusMeta: Record<string, string> = {
    active: "bg-[#e6f4ea] text-[#054d28] dark:bg-[#054d28]/30 dark:text-[#7ed492]",
    invited: "bg-[#e8eefb] text-[#1d4ed8] dark:bg-[#1d4ed8]/25 dark:text-[#a5c6ff]",
    paused: "bg-[#fff3c2] text-[#b86700] dark:bg-[#b86700]/20 dark:text-[#fcd34d]",
    inactive: "bg-muted text-muted-foreground",
  };
  const statusLabel =
    STATUS_OPTIONS.find((s) => s.value === client.status)?.label ?? client.status;

  const skillTags: string[] = client.skill_tags ?? [];
  const clientName = client.student_name ?? "Client";
  const ratingValue = client.coach_rating;

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Breadcrumb */}
        <Link
          to="/app/clients"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Clients
        </Link>

        {/* Hero card */}
        <div className="rounded-[16px] border bg-card p-7">
          <div className="flex flex-wrap items-start gap-5">
            <Avatar className="h-14 w-14 shrink-0">
              <AvatarFallback className={`text-base font-bold ${paletteFor(clientName)}`}>
                {initials(clientName)}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-[32px] font-black leading-tight tracking-tight text-foreground">
                  {clientName}
                </h1>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${statusMeta[client.status] ?? statusMeta.inactive}`}
                >
                  {statusLabel}
                </span>
                {ratingValue != null && (
                  <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold text-foreground">
                    <Star className="h-3 w-3 fill-current text-[#9fe870]" />
                    {fmtRating(ratingValue)} DUPR
                  </span>
                )}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
                {client.student_email && (
                  <a
                    href={`mailto:${client.student_email}`}
                    className="inline-flex items-center gap-1.5 hover:text-foreground"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {client.student_email}
                  </a>
                )}
                {client.phone && (
                  <a
                    href={`tel:${client.phone}`}
                    className="inline-flex items-center gap-1.5 hover:text-foreground"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {client.phone}
                  </a>
                )}
                {client.source && (
                  <span className="inline-flex items-center gap-1">
                    · found via {sourceLabel(client.source).toLowerCase()}
                  </span>
                )}
                {referrer && (
                  <Link
                    to="/app/clients/$clientId"
                    params={{ clientId: referrer.id }}
                    className="inline-flex items-center gap-1 hover:text-foreground"
                  >
                    · via {referrer.student_name}
                  </Link>
                )}
              </div>

              {(skillTags.length > 0 || true) && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {skillTags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground"
                    >
                      {t}
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => setEditCard("rating")}
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="h-3 w-3" /> tag
                  </button>
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-col items-stretch gap-2">
              <Link to="/app/log" search={{ clientId } as never}>
                <Button className="w-full rounded-[10px]">
                  <ClipboardList className="h-4 w-4" /> Log session
                </Button>
              </Link>
              <Button
                variant="outline"
                className="rounded-[10px]"
                disabled={!client.student_email}
                onClick={() => {
                  if (client.student_email)
                    window.location.href = `mailto:${client.student_email}`;
                }}
              >
                <MessageSquare className="h-4 w-4" /> Message
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-muted-foreground">
                    <Pencil className="h-3.5 w-3.5" /> Edit profile
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setEditCard("contact")}>
                    Contact
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setEditCard("rating")}>
                    Rating & play
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setEditCard("lifecycle")}>
                    Status, source & goals
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setAddRelOpen(true)}>
                    Add related person
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Total lessons"
            value={String(stats.totalLessons)}
            sub={earliestLabel ? `since ${earliestLabel}` : "no sessions yet"}
          />
          <StatTile
            label="Lifetime value"
            value={fmtMoney(stats.totalPaidCents)}
            sub={paidCount > 0 ? `↑ ${fmtMoney(avgPerSession)} avg` : "—"}
          />
          <StatTile
            label="Last session"
            value={stats.lastSeen ? daysAgo(stats.lastSeen) : "—"}
            sub={lastSeenParsed ? `${lastSeenParsed.monthShort} ${lastSeenParsed.day}` : "—"}
          />
          <StatTile
            label="Outstanding"
            value={fmtMoney(totalOutstandingCents)}
            sub={
              unpaidCount > 0
                ? `${unpaidCount} lesson${unpaidCount === 1 ? "" : "s"} unbilled`
                : "all paid"
            }
            tone={totalOutstandingCents > 0 ? "outstanding" : undefined}
          />
        </div>

        {/* Tabs */}
        <div className="mt-6 mb-5 border-b border-border">
          <div className="flex flex-wrap gap-6 text-sm">
            <TabBtn
              label="Sessions"
              count={stats.totalLessons}
              active={tab === "sessions"}
              onClick={() => setTab("sessions")}
            />
            <TabBtn label="Notes" active={tab === "notes"} onClick={() => setTab("notes")} />
            <TabBtn
              label="Skills & goals"
              active={tab === "skills"}
              onClick={() => setTab("skills")}
            />
            <TabBtn
              label="Invoices"
              count={payments.length}
              active={tab === "invoices"}
              onClick={() => setTab("invoices")}
            />
            <TabBtn label="Files" active={tab === "files"} onClick={() => setTab("files")} />
            <TabBtn label="Activity log" active={tab === "activity"} onClick={() => setTab("activity")} />
          </div>
        </div>

        {/* SESSIONS */}
        {tab === "sessions" && (
          <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
            <div className="rounded-[16px] border bg-card p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold tracking-tight">Recent sessions</h2>
                {sessions.length > 6 && (
                  <span className="text-xs font-semibold text-muted-foreground">
                    View all {sessions.length} →
                  </span>
                )}
              </div>
              {sessions.length === 0 ? (
                <p className="mt-6 rounded-[12px] border border-dashed px-4 py-8 text-center text-xs text-muted-foreground">
                  No sessions logged yet.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {sessions.slice(0, 6).map((s) => {
                    const parsed = parseIsoDate(s.session_date);
                    const items = sessionMeta.drillsBySession.get(s.id) ?? [];
                    const topDrills = items.slice(0, 3).map((d) => d.drills?.title).filter(Boolean);
                    const sum = sessionMeta.summaryBySession.get(s.id);
                    const sent = !!sum?.sent_at || s.status === "sent";
                    return (
                      <li key={s.id} className="flex items-center gap-4 py-3.5">
                        <div className="w-12 shrink-0 text-center">
                          <p className="font-display text-2xl font-black leading-none tracking-tight text-foreground">
                            {parsed?.day ?? "—"}
                          </p>
                          <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {parsed?.monthShort ?? ""}
                          </p>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold">
                            {items.length} drill{items.length === 1 ? "" : "s"} · 60 min
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {topDrills.length > 0 ? topDrills.join(", ") : "No drills logged"}
                          </p>
                        </div>
                        <span
                          className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                            sent
                              ? "bg-[#e6f4ea] text-[#054d28] dark:bg-[#054d28]/30 dark:text-[#7ed492]"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {sent ? "Sent" : "Draft"}
                        </span>
                        <Link
                          to="/app/sessions/$sessionId"
                          params={{ sessionId: s.id }}
                          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                        >
                          Open <ArrowRight className="h-3 w-3" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="space-y-4">
              <div className="rounded-[16px] border bg-card p-5">
                <h2 className="text-sm font-bold tracking-tight">Working on</h2>
                {topAreas.length === 0 ? (
                  <p className="mt-3 text-xs text-muted-foreground">Nothing logged yet.</p>
                ) : (
                  <ul className="mt-4 space-y-3">
                    {topAreas.map(([title, count]) => {
                      const pct = (count / topAreas[0][1]) * 100;
                      return (
                        <li key={title}>
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="truncate text-sm font-medium">{title}</p>
                          </div>
                          <div className="mt-1.5 h-1 w-full rounded-full bg-muted">
                            <div
                              className="h-1 rounded-full bg-[#9fe870]"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="rounded-[16px] border bg-card p-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold tracking-tight">Coach notes</h2>
                  <button
                    type="button"
                    onClick={() => setTab("notes")}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                </div>
                {client.notes ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                    {client.notes}
                  </p>
                ) : (
                  <p className="mt-3 text-xs text-muted-foreground">
                    No notes yet. Add a few lines about playing style, preferences, or recent
                    matches.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* NOTES */}
        {tab === "notes" && (
          <div className="space-y-4">
            <div className="rounded-[16px] border bg-card p-5">
              <h2 className="text-sm font-bold tracking-tight">Client notes</h2>
              <Textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={6}
                className="mt-3 resize-none rounded-[10px] text-sm"
                placeholder="General notes about this client…"
              />
              <div className="mt-3 flex justify-end">
                <Button size="sm" onClick={saveNotes} className="rounded-[8px]">
                  Save notes
                </Button>
              </div>
            </div>

            <div className="rounded-[16px] border bg-card p-5">
              <h2 className="text-sm font-bold tracking-tight">Re-engagement nudges</h2>
              <label className="mt-3 flex items-center justify-between gap-3 rounded-[10px] border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Skip nudges for this client</p>
                  <p className="text-xs text-muted-foreground">
                    Excludes them from the weekly stale-client scan.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!(client as any)?.reengagement_opt_out}
                  onChange={async (e) => {
                    const { error } = await supabase
                      .from("coach_students" as never)
                      .update({ reengagement_opt_out: e.target.checked } as never)
                      .eq("id", clientId);
                    if (error) toast.error(error.message);
                    else {
                      toast.success(e.target.checked ? "Opted out" : "Opted in");
                      load();
                    }
                  }}
                  className="h-5 w-5"
                />
              </label>
            </div>

            <div className="rounded-[16px] border bg-card p-5">
              <h2 className="text-sm font-bold tracking-tight">Session notes timeline</h2>
              {sessions.filter((s) => s.notes).length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No per-session notes recorded.
                </p>
              ) : (
                <ul className="mt-4 space-y-4">
                  {sessions
                    .filter((s) => s.notes)
                    .map((s) => {
                      const parsed = parseIsoDate(s.session_date);
                      return (
                        <li key={s.id} className="border-l-2 border-[#9fe870]/50 pl-4">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold">{parsed?.full ?? s.session_date}</p>
                            <Link
                              to="/app/sessions/$sessionId"
                              params={{ sessionId: s.id }}
                              className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                            >
                              Open →
                            </Link>
                          </div>
                          <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                            {s.notes}
                          </p>
                        </li>
                      );
                    })}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* SKILLS & GOALS */}
        {tab === "skills" && (
          <div className="space-y-4">
            {client.goals && (
              <div className="rounded-[16px] border bg-card p-5">
                <h2 className="text-sm font-bold tracking-tight">Goals</h2>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                  {client.goals}
                </p>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[16px] border bg-card p-5">
                <h2 className="text-sm font-bold tracking-tight">Top areas worked on</h2>
                {topAreas.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground">Nothing logged yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {topAreas.map(([title, count]) => {
                      const pct = (count / topAreas[0][1]) * 100;
                      return (
                        <li key={title} className="text-sm">
                          <div className="flex items-center justify-between">
                            <span className="truncate">{title}</span>
                            <span className="text-xs text-muted-foreground">
                              {count} session{count !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                            <div className="h-full bg-[#9fe870]" style={{ width: `${pct}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="rounded-[16px] border bg-card p-5">
                <h2 className="text-sm font-bold tracking-tight">Coverage by category</h2>
                <ul className="mt-3 space-y-2">
                  {Object.entries(coverage).map(([type, count]) => {
                    const total = Object.values(coverage).reduce((a, b) => a + b, 0) || 1;
                    const pct = (count / total) * 100;
                    return (
                      <li key={type} className="text-sm">
                        <div className="flex items-center justify-between">
                          <span className="capitalize">{type}s</span>
                          <span className="text-xs text-muted-foreground">{count}</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                          <div className="h-full bg-court" style={{ width: `${pct}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="rounded-[16px] border bg-card p-5">
              <h2 className="text-sm font-bold tracking-tight">Skill level progression</h2>
              {skillProgression.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  Need at least 2 sessions on the same technique to show progression.
                </p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {skillProgression.map(([title, list]) => (
                    <li key={title}>
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-sm font-medium">{title}</span>
                        <span className="text-xs text-muted-foreground">
                          {list.length} sessions
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {list.map((entry, i) => {
                          const color =
                            entry.level === "l3"
                              ? "bg-[#2ead4b]"
                              : entry.level === "l2"
                                ? "bg-[#9fe870]"
                                : entry.level === "basic"
                                  ? "bg-muted-foreground"
                                  : "bg-muted";
                          return (
                            <div
                              key={i}
                              className="flex items-center gap-1"
                              title={`${entry.date} · ${entry.level ?? "unset"}`}
                            >
                              <span className={`h-3 w-3 rounded-full ${color}`} />
                              {i < list.length - 1 && <span className="h-px w-3 bg-border" />}
                            </div>
                          );
                        })}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground" /> basic
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#9fe870]" /> l2
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#2ead4b]" /> l3
                </span>
              </div>
            </div>

            <div className="rounded-[16px] border bg-card p-5">
              <h2 className="text-sm font-bold tracking-tight">All skills logged</h2>
              {skillsList.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">Nothing covered yet.</p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {skillsList.map((s) => (
                    <li
                      key={s.title}
                      className="flex items-center justify-between gap-3 py-2.5 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{s.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.count}× · last on {s.lastDate}
                          {s.level && ` · ${s.level}`}
                          <span className="ml-1 capitalize text-muted-foreground/80">
                            · {s.type}
                          </span>
                        </p>
                      </div>
                      {s.videoUrl && (
                        <a
                          href={s.videoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                        >
                          Video
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[16px] border bg-card p-5">
                <h2 className="text-sm font-bold tracking-tight">Recap status</h2>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-[10px] bg-[#e6f4ea] p-3 dark:bg-[#054d28]/30">
                    <p className="text-xl font-bold text-[#054d28] dark:text-[#7ed492]">
                      {recapStatus.sent}
                    </p>
                    <p className="text-xs text-muted-foreground">Sent</p>
                  </div>
                  <div className="rounded-[10px] bg-muted p-3">
                    <p className="text-xl font-bold">{recapStatus.drafted}</p>
                    <p className="text-xs text-muted-foreground">Drafted</p>
                  </div>
                  <div className="rounded-[10px] bg-[#fdebec] p-3 dark:bg-[#a72027]/20">
                    <p className="text-xl font-bold text-[#a72027] dark:text-[#f87171]">
                      {recapStatus.none}
                    </p>
                    <p className="text-xs text-muted-foreground">Not started</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[16px] border bg-card p-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold tracking-tight">Related people</h2>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground"
                    onClick={() => setAddRelOpen(true)}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>
                {relations.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground">No related people yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {relations.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <Link
                          to="/app/clients/$clientId"
                          params={{ clientId: r.related_client_id }}
                          className="truncate hover:underline"
                        >
                          {r.related?.student_name ?? "(deleted)"}
                          {r.relationship_label && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              — {r.relationship_label}
                            </span>
                          )}
                        </Link>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={async () => {
                            const { error } = await supabase
                              .from("coach_student_relations" as never)
                              .delete()
                              .eq("id", r.id);
                            if (error) toast.error(error.message);
                            else {
                              toast.success("Removed");
                              load();
                            }
                          }}
                          aria-label="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* INVOICES */}
        {tab === "invoices" && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Paid"
                value={fmtMoney(stats.totalPaidCents)}
                sub={`${paidCount} session${paidCount !== 1 ? "s" : ""}`}
                small
              />
              <StatTile
                label="Outstanding"
                value={fmtMoney(totalOutstandingCents)}
                sub={`${unpaidCount} session${unpaidCount !== 1 ? "s" : ""}`}
                small
                tone={totalOutstandingCents > 0 ? "outstanding" : undefined}
              />
              <StatTile
                label="Avg / session"
                value={fmtMoney(avgPerSession)}
                sub={paidCount > 0 ? "across paid" : "—"}
                small
              />
              <div className="rounded-[16px] border bg-card p-4">
                <p className="text-xs text-muted-foreground">Default rate</p>
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="text-sm">$</span>
                  <Input
                    value={rateDraft}
                    onChange={(e) => setRateDraft(e.target.value)}
                    onBlur={saveRate}
                    placeholder="0.00"
                    className="h-8 w-20 text-sm"
                    inputMode="decimal"
                  />
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">Pre-fills new payments</p>
              </div>
            </div>

            <div className="rounded-[16px] border bg-card p-5">
              <h2 className="text-sm font-bold tracking-tight">Revenue (last 6 months)</h2>
              <div className="mt-4 flex h-32 items-end gap-2">
                {monthlyRevenue.months.map((m) => {
                  const h = (m.cents / monthlyRevenue.max) * 100;
                  return (
                    <div key={m.key} className="flex flex-1 flex-col items-center gap-1.5">
                      <div className="flex h-full w-full items-end">
                        <div
                          className="w-full rounded-t bg-[#9fe870]"
                          style={{
                            height: `${h}%`,
                            minHeight: m.cents > 0 ? "4px" : "0",
                          }}
                          title={fmtMoney(m.cents)}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground">{m.label}</span>
                      <span className="text-[10px] font-semibold">
                        {m.cents > 0 ? fmtMoney(m.cents) : "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="overflow-hidden rounded-[16px] border bg-card">
              {sessions.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">
                  Log a session to start tracking payments.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lesson date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Paid on</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => {
                      const pay = sessionMeta.paymentBySession.get(s.id);
                      const parsed = parseIsoDate(s.session_date);
                      return (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium">
                            {parsed?.full ?? s.session_date}
                          </TableCell>
                          <TableCell>
                            {pay ? (
                              fmtMoney(pay.amount_cents, pay.currency)
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="capitalize">
                            {pay?.method ?? <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            {pay?.paid_at ? (
                              <span className="inline-flex items-center gap-1 text-[#2ead4b]">
                                <CheckCircle2 className="h-3.5 w-3.5" />{" "}
                                {new Date(pay.paid_at).toLocaleDateString()}
                              </span>
                            ) : pay ? (
                              <span className="inline-flex items-center gap-1 text-[#a72027] dark:text-[#f87171]">
                                <Circle className="h-3.5 w-3.5" /> Unpaid
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">
                            {pay?.notes ?? ""}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-[8px]"
                              onClick={() => setPayDialog({ sessionId: s.id, existing: pay })}
                            >
                              <DollarSign className="h-3.5 w-3.5" />{" "}
                              {pay?.paid_at ? "Edit" : pay ? "Mark paid" : "Add"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}

        {/* FILES */}
        {tab === "files" && (
          <div className="rounded-[16px] border border-dashed bg-card p-12 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-semibold">No files yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Upload waivers, intake forms, or video links here when you need to.
            </p>
          </div>
        )}

        {tab === "activity" && user && (
          <div className="space-y-4">
            <PublicBalanceCard clientId={clientId} />
            <ClientActivityTab clientId={clientId} coachId={user.id} />
          </div>
        )}
      </div>

      {/* All existing dialogs preserved */}
      <PaymentDialog
        state={payDialog}
        onClose={() => setPayDialog(null)}
        defaultRateCents={(client as any)?.default_rate_cents ?? null}
        coachId={user!.id}
        onSaved={() => {
          setPayDialog(null);
          load();
        }}
      />

      <EditFieldsDialog
        open={editCard === "contact"}
        onOpenChange={(o) => !o && setEditCard(null)}
        title="Edit contact"
        fields={[
          {
            kind: "text",
            key: "phone",
            label: "Phone",
            type: "tel",
            inputMode: "tel",
            placeholder: "(555) 123-4567",
          },
          {
            kind: "select",
            key: "preferred_contact",
            label: "Preferred contact method",
            options: [...CONTACT_METHOD_OPTIONS],
          },
          { kind: "text", key: "birthday", label: "Birthday", type: "date" },
        ]}
        initial={{
          phone: client.phone ?? "",
          preferred_contact: client.preferred_contact ?? "email",
          birthday: client.birthday ?? "",
        }}
        onSave={async (v) => {
          const { error } = await supabase
            .from("coach_students")
            .update({
              phone: v.phone?.trim() || null,
              preferred_contact: v.preferred_contact,
              birthday: v.birthday || null,
            } as never)
            .eq("id", clientId);
          if (error) {
            toast.error(error.message);
            throw error;
          }
          toast.success("Saved");
          load();
        }}
      />

      <EditFieldsDialog
        open={editCard === "rating"}
        onOpenChange={(o) => !o && setEditCard(null)}
        title="Edit rating & play"
        fields={[
          {
            kind: "select",
            key: "coach_rating",
            label: "Coach rating (1.0–8.0)",
            allowNone: true,
            options: RATING_VALUES.map((r) => ({ value: String(r), label: r.toFixed(1) })),
          },
          { kind: "select", key: "dominant_hand", label: "Dominant hand", options: [...HAND_OPTIONS] },
          { kind: "text", key: "dupr_id", label: "DUPR ID", placeholder: "e.g. TB-1234" },
        ]}
        initial={{
          coach_rating: client.coach_rating != null ? String(client.coach_rating) : null,
          dominant_hand: client.dominant_hand ?? "unknown",
          dupr_id: client.dupr_id ?? "",
        }}
        onSave={async (v) => {
          const { error } = await supabase
            .from("coach_students")
            .update({
              coach_rating: v.coach_rating ? Number(v.coach_rating) : null,
              dominant_hand: v.dominant_hand,
              dupr_id: v.dupr_id?.trim() || null,
            } as never)
            .eq("id", clientId);
          if (error) {
            toast.error(error.message);
            throw error;
          }
          toast.success("Saved");
          load();
        }}
      />

      <EditFieldsDialog
        open={editCard === "lifecycle"}
        onOpenChange={(o) => !o && setEditCard(null)}
        title="Edit lifecycle"
        fields={[
          { kind: "select", key: "status", label: "Status", options: [...STATUS_OPTIONS] },
          {
            kind: "select",
            key: "source",
            label: "Source",
            allowNone: true,
            options: [...SOURCE_OPTIONS],
          },
          {
            kind: "custom",
            key: "referred_by_client_id",
            label: "Referred by",
            render: (val, set) => (
              <ClientPicker
                coachId={user!.id}
                excludeId={clientId}
                value={val ?? null}
                onChange={set}
              />
            ),
          },
          {
            kind: "textarea",
            key: "goals",
            label: "Goals",
            rows: 3,
            placeholder: 'e.g. "Get to 4.0", "Play in mixed tournament"',
          },
        ]}
        initial={{
          status: client.status,
          source: client.source ?? null,
          referred_by_client_id: client.referred_by_client_id ?? null,
          goals: client.goals ?? "",
        }}
        onSave={async (v) => {
          const { error } = await supabase
            .from("coach_students")
            .update({
              status: v.status,
              source: v.source,
              referred_by_client_id: v.referred_by_client_id,
              goals: v.goals?.trim() || null,
            } as never)
            .eq("id", clientId);
          if (error) {
            toast.error(error.message);
            throw error;
          }
          toast.success("Saved");
          load();
        }}
      />

      <AddRelationDialog
        open={addRelOpen}
        onOpenChange={setAddRelOpen}
        coachId={user!.id}
        clientId={clientId}
        existingIds={relations.map((r) => r.related_client_id)}
        onSaved={() => {
          setAddRelOpen(false);
          load();
        }}
      />
    </AppShell>
  );
}

function TabBtn({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative -mb-px flex items-center gap-2 border-b-2 pb-3 text-sm font-semibold transition-colors ${
        active
          ? "border-foreground text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
      {count != null && (
        <span
          className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
            active ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function StatTile({
  label,
  value,
  sub,
  tone,
  small,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "outstanding";
  small?: boolean;
}) {
  const outstanding = tone === "outstanding";
  return (
    <Card
      className={`rounded-[16px] border p-5 shadow-none ${
        outstanding
          ? "border-[#9fe870]/40 bg-[rgba(159,232,112,0.15)] dark:bg-[rgba(159,232,112,0.08)]"
          : "bg-card"
      }`}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`mt-2 font-display font-black leading-none tracking-tight text-foreground ${
          small ? "text-[28px]" : "text-[34px]"
        }`}
      >
        {value}
      </p>
      {sub && <p className="mt-3 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

function PaymentDialog({
  state,
  onClose,
  defaultRateCents,
  coachId,
  onSaved,
}: {
  state: { sessionId: string; existing?: PaymentRow } | null;
  onClose: () => void;
  defaultRateCents: number | null;
  coachId: string;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<string>("venmo");
  const [paidDate, setPaidDate] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state) return;
    const e = state.existing;
    setAmount(
      e
        ? (e.amount_cents / 100).toFixed(2)
        : defaultRateCents
          ? (defaultRateCents / 100).toFixed(2)
          : "",
    );
    setMethod(e?.method ?? "venmo");
    setPaidDate(
      e?.paid_at
        ? new Date(e.paid_at).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
    );
    setNotes(e?.notes ?? "");
  }, [state, defaultRateCents]);

  if (!state) return null;

  async function save(markPaid: boolean) {
    if (!state) return;
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (Number.isNaN(cents) || cents < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSaving(true);
    const payload: any = {
      session_id: state.sessionId,
      coach_id: coachId,
      amount_cents: cents,
      method,
      notes: notes || null,
      paid_at: markPaid ? new Date(paidDate).toISOString() : null,
    };
    const { error } = await supabase
      .from("session_payments" as never)
      .upsert(payload, { onConflict: "session_id" } as never);
    setSaving(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(markPaid ? "Payment recorded" : "Saved");
      onSaved();
    }
  }

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Amount (USD)</label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
            />
          </div>
          <div>
            <label className="text-xs font-medium">Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => (
                  <SelectItem key={m} value={m} className="capitalize">
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium">Paid on</label>
            <Input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => save(false)} disabled={saving}>
            Save unpaid
          </Button>
          <Button onClick={() => save(true)} disabled={saving}>
            Mark paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddRelationDialog({
  open,
  onOpenChange,
  coachId,
  clientId,
  existingIds,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  coachId: string;
  clientId: string;
  existingIds: string[];
  onSaved: () => void;
}) {
  const [relatedId, setRelatedId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setRelatedId(null);
      setLabel("");
    }
  }, [open]);

  async function save() {
    if (!relatedId) {
      toast.error("Pick a client");
      return;
    }
    if (existingIds.includes(relatedId)) {
      toast.error("Already added");
      return;
    }
    setBusy(true);
    const { error } = await supabase
      .from("coach_student_relations" as never)
      .insert({
        coach_id: coachId,
        client_id: clientId,
        related_client_id: relatedId,
        relationship_label: label.trim(),
      } as never);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Related person added");
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add related person</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Client</label>
            <div className="mt-1.5">
              <ClientPicker
                coachId={coachId}
                excludeId={clientId}
                value={relatedId}
                onChange={setRelatedId}
                allowNone={false}
                placeholder="Pick a client…"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Relationship</label>
            <Input
              className="mt-1.5"
              placeholder="e.g. spouse, doubles partner, son, friend"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
